import { Grid, Paper, Skeleton, Stack, Typography } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import StyledTable from '../../commun/table';
import NoData from '../../commun/no-data';
import { SearchCompositionDto } from '../../../services/dtos/search-composition.dto';
import { ResultSearchDto } from '../../../services/dtos/result-search.dto';
import { CompositionsDto } from '../../../services/dtos/compositions.dto';
import { searchCompositions, searchTerritoryEstablishments } from '../../../services/composition.service';
import { showAxiosMessageError } from '../../../utils/error-message.util';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { HeadCellDto } from '../../../services/dtos/head-cell.dto';
import CompositionForm from '../../forms/composition/composition.form';
import { CompositionFormDto } from '../../../services/dtos/composition-form.dto';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { Form, Formik } from 'formik';
import InputForm from '../../commun/input-form';
import SendMailDialogForm from '../../forms/sendMail/sendMailDialog.form';
import './composition.ui.scss';
import { canAccessAllTabs } from '../../../utils/access-role.util';
import { MandateGeneralDto } from '../../../services/dtos/mandate-general.dto';
import { MandateEstablishmentDto } from '../../../services/dtos/mandate-establishment-dto';
import { MandateTerritoryDto } from '../../../services/dtos/mandate-territory.dto';
import { MandateType } from '../../../services/dtos/mandate.dto';
import SubCompositionUi from './SubComposition.ui';
import GroupCompositionTableUi from './GroupCompositionTable.ui';
import {
    SearchTerritoryEstablishmentCompositionDto,
    TerritoryEstablishmentCompositionDto,
} from '../../../services/dtos/territory-establishment-composition.dto';

/**
 * Ui de l'onglet composition
 * @constructor
 */
export default function CompositionUi(
    props: Readonly<{
        mandate: MandateGeneralDto | MandateEstablishmentDto | MandateTerritoryDto;
        mandateType?: string | null;
        accessRole?: string;
    }>
) {
    const canSendMail = canAccessAllTabs(props.accessRole);
    const establishmentId = 'establishmentId' in props.mandate ? props.mandate.establishmentId : undefined;
    const territoryId = 'territoryId' in props.mandate ? props.mandate.territoryId : undefined;
    const isBsca = props.mandateType === MandateType.BSCA;
    const groupType = isBsca ? undefined : props.mandateType === MandateType.AG ? 'ag' : undefined;
    const rawGroupId = 'groupId' in props.mandate ? props.mandate.groupId : undefined;
    const groupId = isBsca || establishmentId ? undefined : rawGroupId && rawGroupId !== '0' ? rawGroupId : undefined;
    // Bureau Syndical et Conseil d'Administration sont deux groupes élus distincts : chacun
    // doit être recherché indépendamment, avec son propre tableau et sa propre pagination.
    const bscaGroupIds = isBsca
        ? 'groupIds' in props.mandate && props.mandate.groupIds && props.mandate.groupIds.length > 0
            ? props.mandate.groupIds
            : rawGroupId && rawGroupId !== '0'
              ? [rawGroupId]
              : []
        : [];
    const mandateSearchKey = `${props.mandate.id}-${establishmentId ?? ''}-${groupId ?? ''}-${groupType ?? ''}-${territoryId ?? ''}`;
    //SnackBar pour les messages
    const showMessage = useSnackbars();
    //Searching
    const [searching, setSearching] = useState<boolean>(false);
    // Erreur de chargement
    const [loadingError, setLoadingError] = useState<string>();
    //Critères de recherche onglet composition
    const [searchComposition, setSearchComposition] = useState<SearchCompositionDto>({
        page: 0,
        itemsPerPage: 10,
        mandateId: props.mandate.id,
        etablissementId: establishmentId,
        groupId,
        groupType,
        territoryId,
    });
    // result onglet composition
    const [resultComposition, setResultaComposition] = useState<ResultSearchDto<CompositionsDto>>();
    const [resultTerritoryEstablishments, setResultTerritoryEstablishments] =
        useState<ResultSearchDto<TerritoryEstablishmentCompositionDto>>();
    const [searchTerritoryEstablishment, setSearchTerritoryEstablishment] =
        useState<SearchTerritoryEstablishmentCompositionDto>({
            page: 0,
            itemsPerPage: 10,
            mandateId: props.mandate.id,
            territoryId: territoryId ?? '',
        });
    const [searchingTerritoryEstablishments, setSearchingTerritoryEstablishments] = useState<boolean>(false);
    const [territoryEstablishmentsLoadingError, setTerritoryEstablishmentsLoadingError] = useState<string>();
    // Gestion du dialog pour l'envoie de mail
    const [openSendMail, setOpenSendMail] = React.useState(false);
    const [openSendMailTerritoryEstablishments, setOpenSendMailTerritoryEstablishments] = React.useState(false);
    //Mails
    const [mails, setMails] = useState<string[]>([]);
    const [territoryEstablishmentMails, setTerritoryEstablishmentMails] = useState<string[]>([]);
    const previousMandateSearchKey = useRef(mandateSearchKey);
    const hasLoadedTerritoryEstablishments = useRef(false);

    const displayedCompositionItems = useMemo(() => {
        const items = resultComposition?.items ?? [];

        if (!searchComposition.sort || !searchComposition.order) {
            return items;
        }

        return [...items].sort((left, right) => {
            const leftValue = String(left[searchComposition.sort!] ?? '').toLocaleLowerCase();
            const rightValue = String(right[searchComposition.sort!] ?? '').toLocaleLowerCase();
            const sortResult = leftValue.localeCompare(rightValue, 'fr', {
                numeric: true,
                sensitivity: 'base',
            });

            return searchComposition.order === 'asc' ? sortResult : -sortResult;
        });
    }, [resultComposition?.items, searchComposition.order, searchComposition.sort]);

    useEffect(() => {
        if (previousMandateSearchKey.current === mandateSearchKey) {
            return;
        }

        previousMandateSearchKey.current = mandateSearchKey;
        setResultaComposition(undefined);
        setResultTerritoryEstablishments(undefined);
        setLoadingError(undefined);
        setTerritoryEstablishmentsLoadingError(undefined);
        setMails([]);
        setTerritoryEstablishmentMails([]);
        hasLoadedTerritoryEstablishments.current = false;
        setSearchComposition({
            page: 0,
            itemsPerPage: 10,
            mandateId: props.mandate.id,
            etablissementId: establishmentId,
            groupId,
            groupType,
            territoryId,
        });
        setSearchTerritoryEstablishment({
            page: 0,
            itemsPerPage: 10,
            mandateId: props.mandate.id,
            territoryId: territoryId ?? '',
        });
    }, [mandateSearchKey, props.mandate.id, establishmentId, groupId, groupType, territoryId]);

    //Lancement de la recherche a chaque changement
    useEffect(() => {
        // Bureau Syndical et Conseil d'Administration sont recherchés indépendamment
        // par GroupCompositionTableUi : pas de recherche combinée à lancer ici.
        if (isBsca) {
            return;
        }

        let isMounted = true;

        setSearching(true);
        setLoadingError(undefined);
        const searchCompositionDto: SearchCompositionDto = {
            ...searchComposition,
        };

        searchCompositions(searchCompositionDto)
            .then(result => {
                if (!isMounted) {
                    return;
                }

                const items = result.items.map(item => ({
                    ...item,
                    name: `${item.firstName} ${item.lastName}`,
                }));
                setResultaComposition({ ...result, items });
            })
            .catch(error => {
                if (!isMounted) {
                    return;
                }

                console.error('Erreur lors de la récupération des compositions');
                setResultaComposition({ items: [], totalItems: 0 });
                setLoadingError('Impossible de charger les élus représentants pour ce mandat.');
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                if (isMounted) {
                    setSearching(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [searchComposition, showMessage, isBsca]);

    useEffect(() => {
        if (!territoryId) {
            return;
        }

        setSearchingTerritoryEstablishments(true);
        setTerritoryEstablishmentsLoadingError(undefined);

        searchTerritoryEstablishments(searchTerritoryEstablishment)
            .then(result => {
                hasLoadedTerritoryEstablishments.current = true;
                setResultTerritoryEstablishments(result);
            })
            .catch(error => {
                if (hasLoadedTerritoryEstablishments.current) {
                    return;
                }

                console.error('Erreur lors de la récupération des établissements du territoire');
                setResultTerritoryEstablishments({ items: [], totalItems: 0 });
                setTerritoryEstablishmentsLoadingError(
                    'Impossible de charger les établissements présents dans ce territoire.'
                );
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                setSearchingTerritoryEstablishments(false);
            });
    }, [searchTerritoryEstablishment, showMessage, territoryId]);

    const handlerSearch = (paramForm: CompositionFormDto) => {
        setSearchComposition({
            ...searchComposition,
            paramTitle: paramForm.title,
            paramName: paramForm.name,
            paramLocalAuthority: paramForm.localAuthority,
            page: 0,
        });
    };

    const handlerSearchTerritoryEstablishment = (values: {
        designation?: string;
        president?: string;
        vicePresident?: string;
    }) => {
        hasLoadedTerritoryEstablishments.current = false;
        setTerritoryEstablishmentMails([]);
        setSearchTerritoryEstablishment({
            ...searchTerritoryEstablishment,
            designation: values.designation,
            president: values.president,
            vicePresident: values.vicePresident,
            page: 0,
        });
    };

    //Méthode au changement de la page
    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setSearchComposition({
            ...searchComposition,
            page: newPage,
        });
    };

    //Méthode au changement d'items par page
    const handleChangeItemsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearchComposition({
            ...searchComposition,
            page: 0,
            itemsPerPage: Number.parseInt(event.target.value, 10),
        });
    };

    const handleChangeTerritoryEstablishmentPage = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ) => {
        setSearchTerritoryEstablishment({
            ...searchTerritoryEstablishment,
            page: newPage,
        });
    };

    const handleChangeTerritoryEstablishmentItemsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setSearchTerritoryEstablishment({
            ...searchTerritoryEstablishment,
            page: 0,
            itemsPerPage: Number.parseInt(event.target.value, 10),
        });
    };

    //handler sort
    const handlerSortBy = (order: 'asc' | 'desc', sort: keyof CompositionsDto) => {
        setSearchComposition({
            ...searchComposition,
            order: order,
            sort: sort,
            page: 0,
        });
    };

    const handlerSortTerritoryEstablishmentBy = (
        order: 'asc' | 'desc',
        sort: keyof TerritoryEstablishmentCompositionDto
    ) => {
        setSearchTerritoryEstablishment({
            ...searchTerritoryEstablishment,
            order,
            sort,
            page: 0,
        });
    };

    // Création des cellules têtes du tableau
    // Les ids des cellules doivent correspondre aux noms des champs DTO, sauf l'action
    const headCells: HeadCellDto<CompositionsDto, keyof CompositionsDto>[] = [
        { id: 'name', label: 'Nom', sort: true, width: 30 },
        { id: 'title', label: 'Titre', sort: true, width: 16 },
        { id: 'function', label: 'Fonction', sort: false, width: 18 },
        { id: 'localAuthority', label: 'Collectivité', sort: true, width: 36 },
    ];

    const territoryEstablishmentHeadCells: HeadCellDto<
        TerritoryEstablishmentCompositionDto,
        keyof TerritoryEstablishmentCompositionDto
    >[] = [
        { id: 'designation', label: 'Désignation', sort: true, width: 44 },
        { id: 'president', label: 'Président', sort: false, width: 28 },
        { id: 'vicePresident', label: 'Vice-Président', sort: false, width: 28 },
    ];

    // Permet de lister on non les mails à envoyer
    const checkMail = (composition: CompositionsDto) => {
        //Ajout du mail à la liste
        if (composition.mail && !mails.some(value => value === composition.mail)) {
            const newMails = mails;
            newMails.push(composition.mail);
            setMails(newMails);
        } else if (composition.mail && mails.some(value => value === composition.mail)) {
            setMails(mails.filter(value => value !== composition.mail));
        }
    };

    const checkTerritoryEstablishmentMail = (establishment: TerritoryEstablishmentCompositionDto) => {
        const emailsToToggle = establishment.emails;
        const hasEveryEmail = emailsToToggle.every(email => territoryEstablishmentMails.includes(email));

        if (hasEveryEmail) {
            setTerritoryEstablishmentMails(
                territoryEstablishmentMails.filter(email => !emailsToToggle.includes(email))
            );
            return;
        }

        setTerritoryEstablishmentMails(Array.from(new Set([...territoryEstablishmentMails, ...emailsToToggle])));
    };

    // L'action est fabriqué à la main pour la tableau car il est personnalisable
    const addAllMails = () => {
        type WithComposition = Required<CompositionsDto>;
        const allMails = resultComposition?.items
            ? resultComposition.items.filter((value): value is WithComposition => !!value.mail).map(value => value.mail)
            : [];
        setMails(allMails);
    };

    const clearAllMails = () => {
        setMails([]);
    };

    const checkAllMails = (checked: boolean) => {
        if (checked) {
            addAllMails();
            return;
        }

        clearAllMails();
    };

    const addAllTerritoryEstablishmentMails = () => {
        setTerritoryEstablishmentMails(
            Array.from(new Set(resultTerritoryEstablishments?.items.flatMap(establishment => establishment.emails)))
        );
    };

    const clearAllTerritoryEstablishmentMails = () => {
        setTerritoryEstablishmentMails([]);
    };

    const checkAllTerritoryEstablishmentMails = (checked: boolean) => {
        if (checked) {
            addAllTerritoryEstablishmentMails();
            return;
        }

        clearAllTerritoryEstablishmentMails();
    };

    //Handler qui gère l'ouverture de la popup "Envoyer un mail"
    const handleClickOpenSendMail = () => {
        setOpenSendMail(true);
    };

    //Handler qui gère la fermeture de la popup "Envoyer un mail"
    const handleCloseSendMail = () => {
        setOpenSendMail(false);
    };

    const handleClickOpenSendMailTerritoryEstablishments = () => {
        setOpenSendMailTerritoryEstablishments(true);
    };

    const handleCloseSendMailTerritoryEstablishments = () => {
        setOpenSendMailTerritoryEstablishments(false);
    };

    // L'action est fabriqué le bouton d'envoie de mail
    const checkAction = () => {
        return (
            <Button
                variant="contained"
                data-testid={`check-action`}
                disabled={mails.length === 0}
                onClick={() => handleClickOpenSendMail()}
            >
                Envoyer un mail
            </Button>
        );
    };

    const checkTerritoryEstablishmentAction = () => {
        return (
            <Button
                variant="contained"
                data-testid={`check-territory-establishment-action`}
                disabled={territoryEstablishmentMails.length === 0}
                onClick={() => handleClickOpenSendMailTerritoryEstablishments()}
            >
                Envoyer un mail
            </Button>
        );
    };

    const showTerritoryEstablishmentsTable = () => {
        if (!territoryId) {
            return null;
        }
        if (resultTerritoryEstablishments === undefined) {
            return <Skeleton variant="rectangular" height={250}></Skeleton>;
        }
        if (territoryEstablishmentsLoadingError) {
            return <NoData title={'Information : '} content={territoryEstablishmentsLoadingError} />;
        }
        if (resultTerritoryEstablishments.totalItems === 0) {
            return <NoData title={'Information : '} content={'Aucun établissement trouvé dans ce territoire'} />;
        }

        return (
            <>
                <Typography variant="h3" className={'title composition-ui__secondary-title'}>
                    <GroupsIcon /> Établissements présents dans le territoire
                </Typography>
                <Formik
                    initialValues={{
                        designation: searchTerritoryEstablishment.designation ?? '',
                        president: searchTerritoryEstablishment.president ?? '',
                        vicePresident: searchTerritoryEstablishment.vicePresident ?? '',
                    }}
                    onReset={() =>
                        handlerSearchTerritoryEstablishment({
                            designation: '',
                            president: '',
                            vicePresident: '',
                        })
                    }
                    onSubmit={handlerSearchTerritoryEstablishment}
                >
                    <Form>
                        <Paper className="filter-form" elevation={0}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={3}>
                                    <InputForm id="designation" name="designation" label="Désignation" />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <InputForm id="president" name="president" label="Président" />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <InputForm id="vicePresident" name="vicePresident" label="Vice-Président" />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <div className="filter-form__actions">
                                        <Button
                                            type="reset"
                                            variant="outlined"
                                            className="filter-form__button filter-form__button--reset"
                                            startIcon={<ClearIcon />}
                                        >
                                            Annuler
                                        </Button>
                                        <Button
                                            data-testid={'search-territory-establishments'}
                                            type="submit"
                                            variant="contained"
                                            className="filter-form__button"
                                            startIcon={<SearchIcon />}
                                            disabled={searchingTerritoryEstablishments}
                                        >
                                            {searchingTerritoryEstablishments ? 'Recherche...' : 'Rechercher'}
                                        </Button>
                                    </div>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Form>
                </Formik>
                <StyledTable
                    data={resultTerritoryEstablishments.items}
                    searching={searchingTerritoryEstablishments}
                    headCells={territoryEstablishmentHeadCells}
                    totalItems={resultTerritoryEstablishments.totalItems}
                    page={searchTerritoryEstablishment.page}
                    handleChangePage={handleChangeTerritoryEstablishmentPage}
                    handleChangeRowsPerPage={handleChangeTerritoryEstablishmentItemsPerPage}
                    itemsPerPage={searchTerritoryEstablishment.itemsPerPage}
                    check={canSendMail ? checkTerritoryEstablishmentMail : undefined}
                    checkAll={canSendMail ? checkAllTerritoryEstablishmentMails : undefined}
                    labelCheck={canSendMail ? "Envoyer E-mail aux représentants" : undefined}
                    checkAction={canSendMail ? checkTerritoryEstablishmentAction : undefined}
                    handlerSortBy={handlerSortTerritoryEstablishmentBy}
                    order={searchTerritoryEstablishment.order}
                    sort={searchTerritoryEstablishment.sort}
                ></StyledTable>
                <SendMailDialogForm
                    open={openSendMailTerritoryEstablishments}
                    emails={territoryEstablishmentMails}
                    mandateId={props.mandate.id}
                    handleClose={handleCloseSendMailTerritoryEstablishments}
                />
            </>
        );
    };

    //Premet de conditionner l'affichage du tableau
    const showTable = () => {
        if (resultComposition === undefined) {
            return <Skeleton variant="rectangular" height={250}></Skeleton>;
        }
        if (loadingError) {
            return <NoData title={'Information : '} content={loadingError} />;
        }
        if (resultComposition.totalItems === 0) {
            return <NoData title={'Information : '} content={'Aucune donnée trouvée'} />;
        }

        return (
            <>
                <StyledTable
                    data={displayedCompositionItems}
                    searching={searching}
                    headCells={headCells}
                    totalItems={resultComposition.totalItems}
                    page={searchComposition.page}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeItemsPerPage}
                    itemsPerPage={searchComposition.itemsPerPage}
                    check={canSendMail ? checkMail : undefined}
                    checkAll={canSendMail ? checkAllMails : undefined}
                    labelCheck={canSendMail ? "Envoyer E-mail" : undefined}
                    checkAction={canSendMail ? checkAction : undefined}
                    handlerSortBy={handlerSortBy}
                    order={searchComposition.order}
                    sort={searchComposition.sort}
                ></StyledTable>
                <SendMailDialogForm
                    open={openSendMail}
                    emails={mails}
                    mandateId={props.mandate.id}
                    handleClose={handleCloseSendMail}
                />
            </>
        );
    };

    //Gestion du titre
    let title = '';
    if ('title' in props.mandate) {
        title = props.mandate.title;
    }
    if ('establishmentLabel' in props.mandate) {
        title = props.mandate.establishmentLabel;
    }
    if ('territoryLabel' in props.mandate) {
        title = props.mandate.territoryLabel;
    }

    //Retourne la vue
    return (
        <Grid container className={'tab-content composition-ui'} rowSpacing={1}>
            <Grid item xs={12}>
                <Typography variant="h3" className={'title'}>
                    <GroupsIcon /> Élus représentants {title}
                </Typography>
            </Grid>
            <Grid item xs={12} md={1} />
            <Grid item xs={12} md={10}>
                <Stack>
                    {isBsca ? (
                        bscaGroupIds.length > 0 ? (
                            bscaGroupIds.map((id, index) => (
                                <GroupCompositionTableUi
                                    key={id}
                                    mandateId={props.mandate.id}
                                    groupId={id}
                                    fallbackTitle={`Composition ${index + 1}`}
                                    canSendMail={canSendMail}
                                />
                            ))
                        ) : (
                            <NoData title={'Information : '} content={'Aucune donnée trouvée'} />
                        )
                    ) : (
                        <>
                            {/*On affiche le formulaire*/}
                            <CompositionForm handlerSearch={handlerSearch} searching={searching} />
                            {/* On affiche ensuite les réunions terminées */}
                            {showTable()}
                        </>
                    )}
                    {establishmentId && (
                        <div className="composition-ui__local-authorities">
                            <SubCompositionUi mandate={props.mandate} />
                        </div>
                    )}
                    {showTerritoryEstablishmentsTable()}
                </Stack>
            </Grid>
        </Grid>
    );
}
